import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { Heart } from 'lucide-react';

export default function ScholarshipRequestDialog({ open, onClose, userEmail, userName }) {
  const [request, setRequest] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scholarship-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: userEmail,
          name: userName,
          request_text: request,
          timestamp: new Date().toISOString()
        })
      });

      if (response.ok) {
        toast.success('Scholarship request submitted! We\'ll be in touch soon.');
        setRequest('');
        onClose();
      } else {
        toast.error('Error submitting request');
      }
    } catch (error) {
      toast.error('Error submitting request');
    }
    setIsSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-2">
            <Heart className="h-6 w-6 text-rose-500" />
            Scholarship Support Request
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 mt-4">
          <div className="bg-gradient-to-r from-rose-50 to-pink-50 border border-rose-200 rounded-lg p-6">
            <p className="text-rose-900 leading-relaxed mb-4">
              We believe everyone deserves access to tools for compassionate connection, regardless of financial circumstances. 
              We have a limited number of scholarships and "pay it forward" funds available.
            </p>
            <p className="text-rose-800 leading-relaxed mb-4">
              <span className="font-semibold">Our only ask:</span> Please contribute what you can and commit to the entire course as a matter of trust and mutual respect.
            </p>
            <p className="text-rose-700 italic">
              Please share your situation below. Your request will be reviewed with care and compassion.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">
                Tell us about your situation and what you can contribute:
              </label>
              <Textarea 
                value={request}
                onChange={(e) => setRequest(e.target.value)}
                placeholder="Share as much or as little as feels comfortable. We read every request with an open heart."
                className="min-h-[200px]"
                required
              />
            </div>

            <div className="flex gap-3">
              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
              </Button>
              <Button 
                type="button"
                onClick={onClose}
                variant="outline"
              >
                Cancel
              </Button>
            </div>
          </form>

          <p className="text-sm text-gray-600 text-center italic">
            Blessings, blessings. We're honored to support your journey.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
